import { useLocation, Outlet } from "react-router-dom";
import { LogOut, Loader2 } from "lucide-react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { PaymentWarningBanner } from "./PaymentWarningBanner";
import { PaywallOverlay } from "./PaywallOverlay";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { GoogleCalendarReconnectBadge } from "./GoogleCalendarReconnectBadge";
import { IntegrationReconnectBadge } from "./IntegrationReconnectBadge";
import { ImportProvider } from "@/contexts/ImportContext";
import { AppointmentDraftProvider } from "@/contexts/AppointmentDraftContext";
import { AppointmentDraftHost } from "@/components/appointments/AppointmentDraftHost";
import { ImportProgressBar } from "./ImportProgressBar";
import { ImportProgressDialog } from "@/components/products/ImportProgressDialog";
import { UnreadNotificationBadge } from "./UnreadNotificationBadge";
import { UnreadProvider } from "@/contexts/UnreadContext";
import { TaskDashboardProvider } from "@/contexts/TaskDashboardContext";
import { AppointmentsCalendarFilterProvider } from "@/contexts/AppointmentsCalendarFilterContext";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { WelcomeDialog } from "@/components/onboarding/WelcomeDialog";
import { OnboardingChecklist } from "@/components/onboarding/OnboardingChecklist";
import { OnboardingTip } from "@/components/onboarding/OnboardingTip";
import { SupportAIChat } from "@/components/support-ai/SupportAIChat";
import { useState, useEffect, useRef } from "react";
import { bootstrapEnd } from "@/lib/frontend-telemetry";

function AppLayoutContent() {
  const { user, signOut } = useAuth();
  const { workspaceId, loading: workspaceLoading } = useWorkspace(user?.id);
  const { shouldShowPaywall, loading: subscriptionLoading } = useSubscription();
  const location = useLocation();
  const bootstrapT0 = useRef(performance.now());
  const bootstrapReported = useRef(false);

  const [isSupportOpen, setIsSupportOpen] = useState(false);

  useEffect(() => {
    if (!workspaceLoading && !subscriptionLoading && !bootstrapReported.current) {
      bootstrapReported.current = true;
      bootstrapEnd(bootstrapT0.current);
    }
  }, [workspaceLoading, subscriptionLoading]);

  useEffect(() => {
    const handler = () => setIsSupportOpen((prev) => !prev);
    window.addEventListener("toggle-support-chat", handler);
    return () => window.removeEventListener("toggle-support-chat", handler);
  }, []);

  const isConversationsPage = location.pathname === "/conversations";
  const isLeadsPage = location.pathname === "/leads";
  const isDealsPage = location.pathname === "/deals";
  const isDashboardPage = location.pathname === "/" || location.pathname === "/dashboard";
  const isAppointmentsPage = location.pathname === "/appointments";
  const isSettingsPage = location.pathname === "/settings";
  const isPlansPage = location.pathname === "/planos";
  const isAgentsTestPage = location.pathname === "/agents-test";

  const isTasksPage = location.pathname === "/tasks";
  const isDisparosPage = location.pathname === "/disparos";

  const isFullWidthPage =
    isConversationsPage ||
    isLeadsPage ||
    isDealsPage ||
    isDashboardPage ||
    isAppointmentsPage ||
    isAgentsTestPage ||
    isTasksPage ||
    isDisparosPage;
  
  // Páginas que NÃO devem ter blur (acesso permitido sem assinatura)
  const isPaywallExemptPage = isSettingsPage || isPlansPage;

  // Paywall ativo em página não-isenta = bloquear totalmente (aguarda workspace + assinatura)
  const isBlocked =
    !isPaywallExemptPage &&
    (shouldShowPaywall || subscriptionLoading || workspaceLoading);

  const handleSignOut = async () => {
    await signOut();
  };

  const renderContent = () => {
    // Quando paywall ativo e página não é isenta, não renderizar Outlet (evita queries)
    if (isBlocked) {
      if (subscriptionLoading || workspaceLoading) {
        return (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        );
      }
      return (
        <div className="h-full relative">
          <PaywallOverlay featureName="Esta funcionalidade" />
        </div>
      );
    }

    if (isFullWidthPage) {
      return (
        <div className="h-full flex flex-col">
          <OnboardingTip />
          <div className="flex-shrink-0 pt-0 pb-0 rounded-none shadow-none mx-0 my-0 px-0 py-0">
            <PaymentWarningBanner />
          </div>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <Outlet />
          </div>
        </div>
      );
    }

    return (
      <div className="container p-6 mb-0 mr-0 ml-0 mt-0 my-0 mx-0 py-0 px-0">
        <OnboardingTip />
        <PaymentWarningBanner />
        <Outlet />
      </div>
    );
  };

  // Sempre incluir providers em páginas isentas (ex.: Configurações) para a página carregar; sem assinatura em outras páginas podemos pular para aliviar carga
  const wrapWithOptionalProviders = (content: React.ReactNode) => {
    if (shouldShowPaywall && !isPaywallExemptPage) {
      return content;
    }
    return (
      <ImportProvider>
        <AppointmentDraftProvider workspaceId={workspaceId}>
          <OnboardingProvider workspaceId={workspaceId} userId={user?.id}>
            {content}
          </OnboardingProvider>
        </AppointmentDraftProvider>
      </ImportProvider>
    );
  };

  return wrapWithOptionalProviders(
    <SidebarProvider>
      <div className="h-screen flex w-full min-w-0 overflow-hidden">
        <AppSidebar />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="h-12 sm:h-14 py-0 flex-shrink-0 border-b bg-card flex items-center justify-between px-3 sm:px-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <SidebarTrigger />
              <div></div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationReconnectBadge />
              <GoogleCalendarReconnectBadge />
              <UnreadNotificationBadge />
              <div className="[&_button]:h-8 [&_button]:w-8 sm:[&_button]:h-9 sm:[&_button]:w-9">
                <ThemeToggle />
              </div>
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="h-8 sm:h-9 px-2 sm:px-3">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </header>
          <main className={`flex-1 min-h-0 relative flex flex-col ${isFullWidthPage && !isBlocked ? 'overflow-hidden' : 'overflow-auto'}`}>
            {renderContent()}
          </main>
        </div>
      </div>
      {!shouldShowPaywall && (
        <>
          <ImportProgressBar />
          <ImportProgressDialog />
          <AppointmentDraftHost />
          <WelcomeDialog />
          <OnboardingChecklist />
        </>
      )}
      {workspaceId && <SupportAIChat isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} workspaceId={workspaceId} />}
    </SidebarProvider>
  );
}

export function AppLayout() {
  const { user, loading: authLoading } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { shouldShowPaywall } = useSubscription();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[hsl(240_35%_8%)] to-background flex items-center justify-center">
        <div className="text-center">
          <img src="/logo-light.png?v=6" alt="FabChat" className="h-12 sm:h-16 w-auto mx-auto mb-6 object-contain block dark:hidden" />
          <img src="/logo.png?v=6" alt="FabChat" className="h-12 sm:h-16 w-auto mx-auto mb-6 object-contain hidden dark:block" />
          <p className="text-white/80 font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <UnreadProvider workspaceId={workspaceId} disabled={shouldShowPaywall}>
      <TaskDashboardProvider workspaceId={workspaceId} disabled={shouldShowPaywall}>
        <AppointmentsCalendarFilterProvider
          workspaceId={workspaceId}
          disabled={shouldShowPaywall}
        >
          <AppLayoutContent />
        </AppointmentsCalendarFilterProvider>
      </TaskDashboardProvider>
    </UnreadProvider>
  );
}
